import React, {useContext, useEffect, useState} from "react";
import styles from "./list_view.module.css";
import VariableBox from "../../../components/boxes/variable";
import { BobAssistantDashboard } from "../../../components/bob_assistant";
import ProductionChart from "../../../components/production_chart/production_chart";
import ConsumptionChart from "../../../components/consumption_chart/consumption_chart";
import axiosInstance from "../../../AxiosInstance";
import Modal from "../../../components/modal/modal";
import {TextInput, SelectInput} from "../../../components/input/inputs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBolt, faPlus, faTrash, faPencil, faClipboard, faToggleOn, faToggleOff, faSlidersH, faFont, faPaperPlane} from "@fortawesome/free-solid-svg-icons";
import MyContext from "../../../Context";
import LocationWidget from "../../../components/boxes/location_widget";
import {PlaceholderInput, PlaceholderTextarea} from "../../../components/input/PlaceholderTextarea";
import { useTranslation } from 'react-i18next';

const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: '6px',
    border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none',
    color: '#334155', marginBottom: '10px', boxSizing: 'border-box',
};
const labelStyle = {fontSize: '12px', fontWeight: '600', color: '#64748B', marginBottom: '4px', display: 'block'};

// Multi-pass normalization: handles arbitrarily nested {placeholder} patterns.
// Inside JSON strings → replace innermost {…} with literal x (no quotes added).
// Bare (unquoted) {…} → replace with JSON value "x".
// Iterates until stable so nested cases like {Temp1:{slider_value}} are fully resolved.
function normalizePlaceholders(text) {
    let result = text;
    let prev;
    do {
        prev = result;
        result = result
            .replace(/"[^"]*"/g, m => m.replace(/\{[^{}]*\}/g, 'x'))  // inside strings: {…} → x
            .replace(/\{[^{}]*\}/g, '"x"');                             // bare {…} → "x"
    } while (result !== prev);
    return result;
}

function tryParseJson(text) {
    if (!text || !text.trim()) return null;
    try {
        JSON.parse(normalizePlaceholders(text));
        return null;
    } catch (e) {
        return e.message;
    }
}

function JsonTextarea({label, value, onChange, placeholder, placeholders}) {
    const { t } = useTranslation();
    const error = tryParseJson(value);

    const formatJson = () => {
        if (!value || !value.trim()) return;
        const tokens = {};
        let i = 0;
        let tokenized = value;
        let prev;
        do {
            prev = tokenized;
            // 1. Tokenize entire quoted strings that contain any {…} — handles nested
            //    cases like "{Temp1:{slider_value}}" as a single atomic token.
            tokenized = tokenized.replace(/"[^"]*\{[^{}]*\}[^"]*"/g, (match) => {
                const k = `__PH${i++}__`;
                tokens[`"${k}"`] = match;
                return `"${k}"`;
            });
            // 2. Tokenize bare {placeholder} patterns — but NOT JSON objects (which start
            //    with a " key after the opening brace). [^"{}] ensures the first char
            //    is neither a quote nor a brace, so real JSON objects are skipped.
            tokenized = tokenized.replace(/\{[^"{}][^{}]*\}/g, m => {
                const k = `"__PH${i++}__"`;
                tokens[k] = m;
                return k;
            });
        } while (tokenized !== prev);
        try {
            const pretty = JSON.stringify(JSON.parse(tokenized), null, 2);
            // Multi-pass restore: tokens can reference earlier tokens (nested placeholders)
            let restored = pretty;
            let rPrev;
            do {
                rPrev = restored;
                restored = restored.replace(/"__PH\d+__"/g, m => tokens[m] || m);
            } while (restored !== rPrev);
            onChange(restored);
        } catch (_) {}
    };

    return (
        <div style={{marginTop: '12px', marginBottom: '10px'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px'}}>
                <span style={labelStyle}>{label}</span>
                <button
                    type="button" onClick={formatJson}
                    disabled={!value || !!error}
                    style={{
                        fontSize: '11px', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer',
                        border: '1px solid #C7D2FE', background: '#EEF2FF', color: '#4F46E5',
                        fontWeight: 600, opacity: (!value || !!error) ? 0.4 : 1,
                    }}
                >
                    {t('variables.formatJson')}
                </button>
            </div>
            <PlaceholderTextarea
                value={value || ''}
                onChange={onChange}
                placeholder={placeholder}
                placeholders={placeholders}
                style={{
                    ...inputStyle,
                    fontFamily: 'monospace', fontSize: '12px',
                    minHeight: '80px', resize: 'vertical', marginBottom: error ? '2px' : '0',
                    border: `1px solid ${error ? '#EF4444' : '#E2E8F0'}`,
                    backgroundColor: error ? '#FFF8F8' : '#fff',
                }}
            />
            {error && (
                <div style={{fontSize: '11px', color: '#EF4444', marginBottom: '6px'}}>
                    {t('variables.invalidJson', {error})}
                </div>
            )}
        </div>
    );
}

const EMPTY_FORM = {action_type: '5', api_method: 'POST', name: '', mqtt_connection: '', mqtt_topic: '', mqtt_payload: '', api_connection: '', api_endpoint: '', api_body: '', boolean_format: 'raw', button_type: 'button', slider_min: '0', slider_max: '', linked_variable: '', timeout_seconds: '15'};

function ActionButtonsSection({deviceId, projectId, variables}) {
    const { t } = useTranslation();
    const {dashboards} = useContext(MyContext);
    const [buttons, setButtons] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingButton, setEditingButton] = useState(null); // null = create mode, id = edit mode
    const [form, setForm] = useState(EMPTY_FORM);
    const [mqttConnections, setMqttConnections] = useState([]);
    const [apiConnections, setApiConnections] = useState([]);
    const [triggerState, setTriggerState] = useState({});
    const [showDashboardModal, setShowDashboardModal] = useState(false);
    const [dashboardForm, setDashboardForm] = useState({dashboard_id: '', widget_name: ''});
    const [dashboardTargetBtn, setDashboardTargetBtn] = useState(null);
    const [sliderValues, setSliderValues] = useState({});
    const [toggleStates, setToggleStates] = useState({});
    const [textValues, setTextValues] = useState({});
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [triggerError, setTriggerError] = useState({}); // btn.id → error message

    // Build placeholder chips: {device_serial} + one per variable name
    const placeholders = ['{device_serial}', ...(Array.isArray(variables) ? variables.map(v => `{${v.variable?.name || v.name}}`).filter(Boolean) : [])];

    // Initialize toggle, slider and text states from buttons' last_value / linked variable.
    // Skip buttons currently being triggered (loading/blocked) so the optimistic update
    // is not overwritten by the next MQTT variables refresh.
    useEffect(() => {
        if (!buttons.length) return;
        const newToggles = {};
        const newSliders = {};
        const newTexts = {};
        buttons.forEach(btn => {
            // Never override state while a trigger is in-flight
            const ts = triggerState[btn.id];
            if (ts === 'loading' || ts === 'blocked') return;

            // Prefer linked variable value if available
            if (btn.linked_variable) {
                const vd = (Array.isArray(variables) ? variables : []).find(v => (v.variable?.id ?? v.variable) === btn.linked_variable);
                if (vd) {
                    if (btn.button_type === 'toggle') {
                        const raw = String(vd.value);
                        newToggles[btn.id] = raw === '1' || raw === 'true' || raw === 'True';
                    } else if (btn.button_type === 'slider') {
                        const parsed = parseFloat(vd.value);
                        if (!isNaN(parsed)) newSliders[btn.id] = parsed;
                    }
                    return;
                }
            }
            // Fallback to last_value from DB
            if (btn.last_value !== null && btn.last_value !== undefined) {
                if (btn.button_type === 'toggle') {
                    newToggles[btn.id] = btn.last_value === '1' || btn.last_value === 'true';
                } else if (btn.button_type === 'slider') {
                    const parsed = parseFloat(btn.last_value);
                    if (!isNaN(parsed)) newSliders[btn.id] = parsed;
                } else if (btn.button_type === 'text') {
                    newTexts[btn.id] = btn.last_value;
                }
            }
        });
        setToggleStates(prev => ({...prev, ...newToggles}));
        setSliderValues(prev => ({...prev, ...newSliders}));
        setTextValues(prev => ({...prev, ...newTexts}));
    }, [buttons, variables, triggerState]);

    useEffect(() => {
        const toArray = d => Array.isArray(d) ? d : (d?.results || []);
        axiosInstance.get(`device_action_button/?device_id=${deviceId}`).then(res => setButtons(toArray(res.data)));
        axiosInstance.get(`mqtt_connection/?project_id=${projectId}`).then(res => setMqttConnections(toArray(res.data)));
        axiosInstance.get(`api_connection/?project_id=${projectId}`).then(res => setApiConnections(toArray(res.data)));
    }, [deviceId, projectId]);

    const triggerWithWait = async (btn, sliderValue) => {
        const state = triggerState[btn.id];
        if (state === 'loading' || state === 'blocked') return;

        setTriggerState(prev => ({...prev, [btn.id]: 'loading'}));

        // Find linked variable's current VariableData and value
        let linkedVdId = null;
        let originalValue = null;
        if (btn.linked_variable) {
            const vd = (Array.isArray(variables) ? variables : []).find(v => {
                const varId = v.variable?.id ?? v.variable;
                return varId === btn.linked_variable;
            });
            if (vd) {
                linkedVdId = vd.id;
                originalValue = vd.value;
            }
        }

        const payload = {};
        if (sliderValue !== undefined && sliderValue !== null) {
            payload.slider_value = sliderValue;
        }

        // Toggle: compute next state and include in payload
        let nextToggleState = null;
        if (btn.button_type === 'toggle') {
            const currentToggle = toggleStates[btn.id] ?? false;
            nextToggleState = !currentToggle;
            payload.toggle_value = nextToggleState;
        }

        // Text: include current text value in payload
        if (btn.button_type === 'text') {
            payload.text_value = textValues[btn.id] ?? '';
        }

        try {
            await axiosInstance.post(`device_action_button/${btn.id}/trigger/`, payload);
            setTriggerState(prev => ({...prev, [btn.id]: 'success'}));

            // Update local toggle state immediately (optimistic) AND sync buttons state
            // so the useEffect doesn't reset the toggle on the next variables refresh.
            if (nextToggleState !== null) {
                const newLastValue = nextToggleState ? '1' : '0';
                setToggleStates(prev => ({...prev, [btn.id]: nextToggleState}));
                setButtons(prev => prev.map(b => b.id === btn.id ? {...b, last_value: newLastValue} : b));
                axiosInstance.patch(`device_action_button/${btn.id}/`, {last_value: newLastValue}).catch(() => {});
            }
            // For slider, sync buttons state immediately
            if (btn.button_type === 'slider' && sliderValue !== undefined && sliderValue !== null) {
                const newLastValue = String(sliderValue);
                setButtons(prev => prev.map(b => b.id === btn.id ? {...b, last_value: newLastValue} : b));
                axiosInstance.patch(`device_action_button/${btn.id}/`, {last_value: newLastValue}).catch(() => {});
            }
            // For text, sync buttons state immediately
            if (btn.button_type === 'text') {
                const newLastValue = textValues[btn.id] ?? '';
                setButtons(prev => prev.map(b => b.id === btn.id ? {...b, last_value: newLastValue} : b));
                axiosInstance.patch(`device_action_button/${btn.id}/`, {last_value: newLastValue}).catch(() => {});
            }

            if (linkedVdId !== null) {
                // Block and poll until variable changes or timeout
                setTriggerState(prev => ({...prev, [btn.id]: 'blocked'}));
                const timeoutMs = (parseInt(btn.timeout_seconds) || 15) * 1000;
                const deadline = Date.now() + timeoutMs;
                let changed = false;
                while (Date.now() < deadline && !changed) {
                    await new Promise(r => setTimeout(r, 1500));
                    try {
                        const res = await axiosInstance.get(`variable_data/?device_id=${deviceId}`);
                        const vds = Array.isArray(res.data) ? res.data : (res.data?.results || []);
                        const vd = vds.find(v => v.id === linkedVdId);
                        if (vd && String(vd.value) !== String(originalValue)) {
                            changed = true;
                            // Sync toggle state with actual variable value
                            if (btn.button_type === 'toggle') {
                                const raw = String(vd.value);
                                setToggleStates(prev => ({...prev, [btn.id]: raw === '1' || raw === 'true' || raw === 'True'}));
                            }
                        }
                    } catch (_) {}
                }
                setTriggerState(prev => ({...prev, [btn.id]: null}));
            } else {
                setTimeout(() => setTriggerState(prev => ({...prev, [btn.id]: null})), 2000);
            }
        } catch (err) {
            setTriggerState(prev => ({...prev, [btn.id]: 'error'}));
            // Surface backend error message so user can diagnose MQTT/API config issues
            const msg = err?.response?.data?.error || err?.response?.data?.detail || err?.message || t('common.failed');
            setTriggerError(prev => ({...prev, [btn.id]: msg}));
            // Revert optimistic toggle state on error
            if (nextToggleState !== null) {
                setToggleStates(prev => ({...prev, [btn.id]: !nextToggleState}));
            }
            setTimeout(() => {
                setTriggerState(prev => ({...prev, [btn.id]: null}));
                setTriggerError(prev => ({...prev, [btn.id]: null}));
            }, 4000);
        }
    };

    const deleteButton = (btnId) => {
        axiosInstance.delete(`device_action_button/${btnId}/`).then(() => {
            setButtons(prev => prev.filter(b => b.id !== btnId));
        });
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingButton(null);
        setForm(EMPTY_FORM);
    };

    const openEdit = (btn) => {
        setEditingButton(btn.id);
        setForm({
            action_type: String(btn.action_type),
            api_method: btn.api_method || 'POST',
            name: btn.name || '',
            mqtt_connection: btn.mqtt_connection ? String(btn.mqtt_connection) : '',
            mqtt_topic: btn.mqtt_topic || '',
            mqtt_payload: btn.mqtt_payload || '',
            api_connection: btn.api_connection ? String(btn.api_connection) : '',
            api_endpoint: btn.api_endpoint || '',
            api_body: btn.api_body || '',
            boolean_format: btn.boolean_format || 'raw',
            button_type: btn.button_type || 'button',
            slider_min: btn.slider_min !== undefined && btn.slider_min !== null ? String(btn.slider_min) : '0',
            slider_max: btn.slider_max !== undefined && btn.slider_max !== null ? String(btn.slider_max) : '',
            linked_variable: btn.linked_variable ? String(btn.linked_variable) : '',
            timeout_seconds: btn.timeout_seconds !== undefined ? String(btn.timeout_seconds) : '15',
        });
        setShowModal(true);
    };

    const saveButton = () => {
        const payload = {
            ...form,
            device: deviceId,
            project: projectId,
            action_type: parseInt(form.action_type),
            slider_min: parseFloat(form.slider_min) || 0,
            slider_max: form.slider_max !== '' && form.slider_max !== null && form.slider_max !== undefined ? parseFloat(form.slider_max) : null,
            linked_variable: form.linked_variable || null,
            mqtt_connection: form.mqtt_connection || null,
            api_connection: form.api_connection || null,
            timeout_seconds: parseInt(form.timeout_seconds) || 15,
        };
        if (editingButton) {
            axiosInstance.patch(`device_action_button/${editingButton}/`, payload).then(res => {
                setButtons(prev => prev.map(b => b.id === editingButton ? res.data : b));
                closeModal();
            });
        } else {
            axiosInstance.post('device_action_button/', payload).then(res => {
                setButtons(prev => [...prev, res.data]);
                closeModal();
            });
        }
    };

    const openDashboardModal = (btn) => {
        setDashboardTargetBtn(btn);
        setDashboardForm({dashboard_id: '', widget_name: btn.name});
        setShowDashboardModal(true);
    };

    const addToDashboard = () => {
        if (!dashboardForm.dashboard_id || !dashboardTargetBtn) return;
        axiosInstance.post(`dashboard/${dashboardForm.dashboard_id}/add_widget/`, {
            name: dashboardForm.widget_name || dashboardTargetBtn.name,
            key: dashboardTargetBtn.id,
            type: 8,
        }).then(() => setShowDashboardModal(false));
    };

    const actionType = parseInt(form.action_type);

    return (
        <div style={{marginTop: '24px'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px'}}>
                <div style={{fontWeight: '600', fontSize: '15px', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <FontAwesomeIcon icon={faBolt} color={'#4F46E5'}/>
                    {t('variables.actionButtons')}
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '6px 14px', borderRadius: '8px',
                        border: '1px solid #C7D2FE', background: '#EEF2FF',
                        cursor: 'pointer', color: '#4F46E5', fontSize: '13px', fontWeight: '500'
                    }}
                >
                    <FontAwesomeIcon icon={faPlus}/>
                    {t('variables.newButton')}
                </button>
            </div>

            <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start'}}>
                {buttons.map(btn => {
                    const state = triggerState[btn.id];
                    const isBlocked = state === 'loading' || state === 'blocked';
                    const isSlider = btn.button_type === 'slider';
                    const isToggle = btn.button_type === 'toggle';
                    const sliderMin = btn.slider_min !== undefined && btn.slider_min !== null ? btn.slider_min : 0;
                    const sliderMax = btn.slider_max !== undefined && btn.slider_max !== null ? btn.slider_max : 999999;
                    const currentSliderVal = sliderValues[btn.id] !== undefined ? sliderValues[btn.id] : sliderMin;
                    const isOn = toggleStates[btn.id] ?? false;

                    const isText = btn.button_type === 'text';
                    const actions = (
                        <div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginTop: (isSlider || isToggle || isText) ? '6px' : '0'}}>
                                <FontAwesomeIcon icon={faPencil} color={'#94A3B8'} style={{cursor: 'pointer', fontSize: '11px'}} onClick={() => openEdit(btn)}/>
                                <FontAwesomeIcon icon={faTrash} color={'#CBD5E1'} style={{cursor: 'pointer', fontSize: '12px'}} onClick={() => setConfirmDeleteId(btn.id)}/>
                                <FontAwesomeIcon icon={faClipboard} color={'#94A3B8'} style={{cursor: 'pointer', fontSize: '11px'}} title="Add to dashboard" onClick={() => openDashboardModal(btn)}/>
                            </div>
                            {triggerError[btn.id] && (
                                <div style={{fontSize: '10px', color: '#EF4444', marginTop: '4px', wordBreak: 'break-word', maxWidth: '220px'}}>
                                    {triggerError[btn.id]}
                                </div>
                            )}
                        </div>
                    );

                    if (isToggle) {
                        const switchColor = isBlocked ? '#A5B4FC' : isOn ? '#4F46E5' : '#CBD5E1';
                        return (
                            <div key={btn.id} style={{
                                display: 'flex', flexDirection: 'column', gap: '2px',
                                background: '#F8FAFC', border: `1px solid ${isOn ? '#C7D2FE' : '#E2E8F0'}`,
                                borderRadius: '12px', padding: '10px 14px', minWidth: '140px',
                                transition: 'border-color 0.2s',
                            }}>
                                <div style={{fontSize: '12px', fontWeight: '600', color: '#334155', marginBottom: '6px'}}>{btn.name}</div>
                                {/* Toggle Switch */}
                                <div
                                    onClick={() => !isBlocked && triggerWithWait(btn)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        cursor: isBlocked ? 'not-allowed' : 'pointer', opacity: isBlocked ? 0.6 : 1,
                                    }}
                                >
                                    <div style={{
                                        position: 'relative', width: '44px', height: '24px',
                                        borderRadius: '12px', background: switchColor,
                                        transition: 'background 0.25s', flexShrink: 0,
                                    }}>
                                        <div style={{
                                            position: 'absolute', top: '3px',
                                            left: isOn ? '23px' : '3px',
                                            width: '18px', height: '18px',
                                            borderRadius: '50%', background: '#fff',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                                            transition: 'left 0.25s',
                                        }}/>
                                    </div>
                                    <span style={{fontSize: '12px', fontWeight: '600', color: isOn ? '#4F46E5' : '#94A3B8', transition: 'color 0.2s'}}>
                                        {state === 'loading' ? '...' : state === 'blocked' ? t('common.waiting', '...') : isOn ? t('variables.toggleOn', 'ON') : t('variables.toggleOff', 'OFF')}
                                    </span>
                                </div>
                                {actions}
                            </div>
                        );
                    }

                    if (isText) {
                        return (
                            <div key={btn.id} style={{
                                display: 'flex', flexDirection: 'column', gap: '6px',
                                background: '#F8FAFC', border: '1px solid #E2E8F0',
                                borderRadius: '10px', padding: '10px 14px', minWidth: '200px',
                                opacity: isBlocked ? 0.6 : 1,
                            }}>
                                <div style={{fontSize: '12px', fontWeight: '600', color: '#334155'}}>{btn.name}</div>
                                <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                                    <input
                                        type="text"
                                        value={textValues[btn.id] ?? ''}
                                        disabled={isBlocked}
                                        onChange={e => setTextValues(prev => ({...prev, [btn.id]: e.target.value}))}
                                        onKeyDown={e => { if (e.key === 'Enter' && !isBlocked) triggerWithWait(btn); }}
                                        placeholder={t('variables.textPlaceholder', 'Escribe el valor...')}
                                        style={{
                                            flex: 1, padding: '6px 10px', borderRadius: '6px',
                                            border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none',
                                            color: '#334155', boxSizing: 'border-box',
                                            cursor: isBlocked ? 'not-allowed' : 'text',
                                        }}
                                    />
                                    <button
                                        onClick={() => !isBlocked && triggerWithWait(btn)}
                                        disabled={isBlocked}
                                        style={{
                                            background: state === 'success' ? '#22C55E' : state === 'error' ? '#EF4444' : '#4F46E5',
                                            color: '#fff', border: 'none', borderRadius: '6px',
                                            padding: '6px 10px', cursor: isBlocked ? 'not-allowed' : 'pointer',
                                            fontSize: '13px', transition: 'background 0.2s', flexShrink: 0,
                                        }}
                                    >
                                        {state === 'loading' ? '...' : state === 'success' ? '✓' : state === 'error' ? '✗' : <FontAwesomeIcon icon={faPaperPlane}/>}
                                    </button>
                                </div>
                                {state === 'blocked' && (
                                    <div style={{fontSize: '11px', color: '#4F46E5'}}>{t('common.waiting', 'Esperando...')}</div>
                                )}
                                {actions}
                            </div>
                        );
                    }

                    if (isSlider) {
                        return (
                            <div key={btn.id} style={{
                                display: 'flex', flexDirection: 'column', gap: '4px',
                                background: '#F8FAFC', border: '1px solid #E2E8F0',
                                borderRadius: '10px', padding: '10px 14px', minWidth: '180px',
                                opacity: isBlocked ? 0.6 : 1,
                            }}>
                                <div style={{fontSize: '12px', fontWeight: '600', color: '#334155', marginBottom: '2px'}}>{btn.name}</div>
                                <input
                                    type="range"
                                    min={sliderMin} max={sliderMax} step={1}
                                    value={currentSliderVal}
                                    disabled={isBlocked}
                                    onChange={e => setSliderValues(prev => ({...prev, [btn.id]: parseFloat(e.target.value)}))}
                                    onMouseUp={e => { if (!isBlocked) triggerWithWait(btn, parseFloat(e.target.value)); }}
                                    onTouchEnd={e => { if (!isBlocked) triggerWithWait(btn, currentSliderVal); }}
                                    style={{width: '100%', accentColor: '#4F46E5', cursor: isBlocked ? 'not-allowed' : 'pointer'}}
                                />
                                <input
                                    type="number"
                                    min={sliderMin} max={sliderMax !== 999999 ? sliderMax : undefined}
                                    value={currentSliderVal}
                                    disabled={isBlocked}
                                    onChange={e => setSliderValues(prev => ({...prev, [btn.id]: parseFloat(e.target.value) || 0}))}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && !isBlocked) triggerWithWait(btn, parseFloat(e.target.value) || 0);
                                    }}
                                    onBlur={e => {
                                        const val = parseFloat(e.target.value) || 0;
                                        if (!isBlocked && val !== (sliderValues[`${btn.id}_last`] ?? sliderMin)) {
                                            setSliderValues(prev => ({...prev, [`${btn.id}_last`]: val}));
                                            triggerWithWait(btn, val);
                                        }
                                    }}
                                    style={{
                                        ...inputStyle, marginBottom: 0,
                                        textAlign: 'center', width: '100%',
                                        cursor: isBlocked ? 'not-allowed' : 'auto',
                                        color: state === 'blocked' ? '#4F46E5' : '#334155',
                                    }}
                                />
                                {state === 'blocked' && (
                                    <div style={{fontSize: '11px', color: '#4F46E5', textAlign: 'center'}}>{t('common.waiting', 'Esperando...')}</div>
                                )}
                                {actions}
                            </div>
                        );
                    }

                    const bgColor = state === 'success' ? '#22C55E' : state === 'error' ? '#EF4444' : state === 'blocked' ? '#6366F1' : '#4F46E5';
                    const label = state === 'success' ? t('common.done') : state === 'error' ? t('common.failed') : state === 'blocked' ? '...' : btn.name;
                    return (
                        <div key={btn.id} style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                            <button
                                onClick={() => triggerWithWait(btn)}
                                disabled={isBlocked}
                                style={{
                                    backgroundColor: bgColor, color: '#fff', border: 'none',
                                    borderRadius: '8px', padding: '8px 18px', fontSize: '14px',
                                    fontWeight: '600', cursor: isBlocked ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.3s', boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                }}
                            >
                                {state === 'loading' ? '...' : label}
                            </button>
                            {actions}
                        </div>
                    );
                })}
            </div>

            {confirmDeleteId && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '12px', padding: '24px 28px',
                        maxWidth: '340px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    }}>
                        <div style={{fontSize: '15px', fontWeight: '700', color: '#1E293B', marginBottom: '8px'}}>
                            {t('variables.deleteButtonTitle', '¿Eliminar botón?')}
                        </div>
                        <div style={{fontSize: '13px', color: '#64748B', marginBottom: '20px'}}>
                            {t('variables.deleteButtonConfirm', 'Esta acción no se puede deshacer.')}
                        </div>
                        <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                            <button
                                onClick={() => setConfirmDeleteId(null)}
                                style={{
                                    padding: '7px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                                    border: '1px solid #E2E8F0', background: '#F8FAFC', color: '#64748B', cursor: 'pointer',
                                }}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={() => { deleteButton(confirmDeleteId); setConfirmDeleteId(null); }}
                                style={{
                                    padding: '7px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                                    border: 'none', background: '#EF4444', color: '#fff', cursor: 'pointer',
                                }}
                            >
                                {t('common.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Modal show={showDashboardModal} setter={setShowDashboardModal} name={t('variables.addToDashboard')} buttonName={t('common.add')} onClick={addToDashboard}>
                <SelectInput name={t('dashboard.title')} internalName={'dashboard_id'} form={dashboardForm} setForm={setDashboardForm} options={dashboards}/>
                <TextInput name={t('common.name')} internalName={'widget_name'} form={dashboardForm} setForm={setDashboardForm}/>
            </Modal>

            <Modal show={showModal} setter={(v) => { if (!v) closeModal(); }} name={editingButton ? t('variables.editActionButton') : t('variables.newActionButton')} buttonName={editingButton ? t('common.save') : t('common.create')} onClick={saveButton}>
                <label style={labelStyle}>{t('variables.buttonName')}</label>
                <input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Turn ON" style={inputStyle}/>

                <label style={labelStyle}>{t('variables.buttonType', 'Tipo de control')}</label>
                <div style={{display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap'}}>
                    {[
                        {v: 'button', l: t('variables.typeButton', 'Botón'), icon: faBolt},
                        {v: 'toggle', l: t('variables.typeToggle', 'Interruptor'), icon: faToggleOn},
                        {v: 'slider', l: t('variables.typeSlider', 'Slider'), icon: faSlidersH},
                        {v: 'text', l: t('variables.typeText', 'Texto'), icon: faFont},
                    ].map(opt => (
                        <label key={opt.v} style={{
                            display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                            padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                            border: `1px solid ${form.button_type === opt.v ? '#C7D2FE' : '#E2E8F0'}`,
                            backgroundColor: form.button_type === opt.v ? '#EEF2FF' : '#F8FAFC',
                            color: form.button_type === opt.v ? '#4F46E5' : '#64748B',
                            transition: 'all 0.15s',
                        }}>
                            <input type="radio" name="button_type" value={opt.v} checked={form.button_type === opt.v} onChange={() => setForm({...form, button_type: opt.v})} style={{display: 'none'}}/>
                            <FontAwesomeIcon icon={opt.icon} style={{fontSize: '13px'}}/>
                            {opt.l}
                        </label>
                    ))}
                </div>

                {form.button_type === 'slider' && (
                    <div style={{display: 'flex', gap: '10px', marginBottom: '10px'}}>
                        <div style={{flex: 1}}>
                            <label style={labelStyle}>{t('variables.sliderMin', 'Mínimo')}</label>
                            <input type="number" value={form.slider_min} onChange={e => setForm({...form, slider_min: e.target.value})} placeholder="0" style={{...inputStyle, marginBottom: 0}}/>
                        </div>
                        <div style={{flex: 1}}>
                            <label style={labelStyle}>{t('variables.sliderMax', 'Máximo (opcional)')}</label>
                            <input type="number" value={form.slider_max} onChange={e => setForm({...form, slider_max: e.target.value})} placeholder={t('variables.noMax', 'Sin límite')} style={{...inputStyle, marginBottom: 0}}/>
                        </div>
                    </div>
                )}

                {(() => {
                    // Build modal placeholders: base + type-specific special values
                    const modalPlaceholders = [
                        ...placeholders,
                        ...(form.button_type === 'toggle' ? ['{toggle_value}'] : []),
                        ...(form.button_type === 'slider' ? ['{slider_value}'] : []),
                        ...(form.button_type === 'text' ? ['{text_value}', '"{text_value}"'] : []),
                    ];
                    return (<>
                <label style={labelStyle}>{t('variables.actionType')}</label>
                <select value={form.action_type} onChange={e => setForm({...form, action_type: e.target.value})} style={inputStyle}>
                    <option value="5">MQTT</option>
                    <option value="6">API</option>
                </select>

                {actionType === 5 && (
                    <>
                        <label style={labelStyle}>{t('variables.mqttConnection')}</label>
                        <select value={form.mqtt_connection} onChange={e => setForm({...form, mqtt_connection: e.target.value})} style={inputStyle}>
                            <option value="">{t('variables.selectConnection')}</option>
                            {mqttConnections.map(c => <option key={c.id} value={c.id}>{c.name || c.broker}</option>)}
                        </select>
                        <label style={labelStyle}>{t('variables.topic')}</label>
                        <PlaceholderInput
                            value={form.mqtt_topic || ''} onChange={v => setForm({...form, mqtt_topic: v})}
                            placeholder="devices/{device_serial}/cmd" style={{...inputStyle, marginBottom: '4px'}}
                            placeholders={modalPlaceholders}
                        />
                        <JsonTextarea
                            label={t('variables.payload')}
                            value={form.mqtt_payload}
                            onChange={v => setForm({...form, mqtt_payload: v})}
                            placeholder={'{"serial":"{device_serial}","cmd":"{toggle_value}"}'}
                            placeholders={modalPlaceholders}
                        />
                    </>
                )}
                {actionType === 6 && (
                    <>
                        <label style={labelStyle}>{t('variables.apiConnection')}</label>
                        <select value={form.api_connection} onChange={e => setForm({...form, api_connection: e.target.value})} style={inputStyle}>
                            <option value="">{t('variables.selectConnection')}</option>
                            {apiConnections.map(c => <option key={c.id} value={c.id}>{c.name || c.base_url}</option>)}
                        </select>
                        <label style={labelStyle}>{t('variables.method')}</label>
                        <select value={form.api_method} onChange={e => setForm({...form, api_method: e.target.value})} style={inputStyle}>
                            {['GET','POST','PUT','PATCH','DELETE'].map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <label style={labelStyle}>{t('variables.endpointPath')}</label>
                        <PlaceholderInput
                            value={form.api_endpoint || ''} onChange={v => setForm({...form, api_endpoint: v})}
                            placeholder="/webhook/{device_serial}" style={{...inputStyle, marginBottom: '4px'}}
                            placeholders={modalPlaceholders}
                        />
                        <JsonTextarea
                            label={t('variables.bodyJson')}
                            value={form.api_body}
                            onChange={v => setForm({...form, api_body: v})}
                            placeholder={'{"serial":"{device_serial}","value":"{toggle_value}"}'}
                            placeholders={modalPlaceholders}
                        />
                    </>
                )}
                    </>);
                })()}
                <div style={{borderTop: '1px solid #F1F5F9', marginTop: '12px', paddingTop: '12px'}}>
                    <label style={labelStyle}>{t('variables.booleanVarFormat')}</label>
                    <div style={{display: 'flex', gap: '8px'}}>
                        {[{v: 'raw', l: '0 / 1'}, {v: 'bool', l: 'True / False'}, {v: 'lower', l: 'true / false'}].map(opt => (
                            <label key={opt.v} style={{
                                display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                                padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500',
                                border: `1px solid ${form.boolean_format === opt.v ? '#C7D2FE' : '#E2E8F0'}`,
                                backgroundColor: form.boolean_format === opt.v ? '#EEF2FF' : '#F8FAFC',
                                color: form.boolean_format === opt.v ? '#4F46E5' : '#64748B',
                            }}>
                                <input
                                    type="radio" name="boolean_format" value={opt.v}
                                    checked={form.boolean_format === opt.v}
                                    onChange={() => setForm({...form, boolean_format: opt.v})}
                                    style={{accentColor: '#4F46E5'}}
                                />
                                {opt.l}
                            </label>
                        ))}
                    </div>
                </div>

                <div style={{borderTop: '1px solid #F1F5F9', marginTop: '12px', paddingTop: '12px'}}>
                    <label style={labelStyle}>{t('variables.linkedVariable', 'Variable vinculada (para bloquear hasta confirmación)')}</label>
                    <select value={form.linked_variable} onChange={e => setForm({...form, linked_variable: e.target.value})} style={inputStyle}>
                        <option value="">{t('variables.noLinkedVariable', 'Sin variable vinculada')}</option>
                        {(Array.isArray(variables) ? variables : [])
                            .filter(v => {
                                const vType = v.variable?.type ?? v.type;
                                if (form.button_type === 'slider') return vType === 0;
                                if (form.button_type === 'text') return vType === 3;
                                return vType === 5; // button / toggle → bool
                            })
                            .map(v => {
                                const varId = v.variable?.id ?? v.variable;
                                const varName = v.variable?.name || v.name;
                                return <option key={varId} value={varId}>{varName}</option>;
                            })
                        }
                    </select>
                    <label style={labelStyle}>{t('variables.timeoutSeconds', 'Timeout (segundos)')}</label>
                    <input
                        type="number" min="1" value={form.timeout_seconds}
                        onChange={e => setForm({...form, timeout_seconds: e.target.value})}
                        style={{...inputStyle, width: '120px'}}
                        placeholder="15"
                    />
                </div>
            </Modal>
        </div>
    );
}

export default function VariableListView({data, setLocked, onHide}) {
    const device = JSON.parse(localStorage.getItem('device'));

    // Verificar si es un dispositivo BOB ASSISTANT usando el nuevo campo dinámico
    const isBobAssistant = device?.type?.is_predictive_maintenance &&
        device?.type?.predictive_maintenance_system?.handler_key === 'bob_assistant';

    // Check if any variable has production counter configured
    const hasProductionCounter = Array.isArray(data) && data.some(v => v.is_production_counter);
    const hasConsumptionCounter = Array.isArray(data) && data.some(v => v.is_consumption_counter);

    const projectId = localStorage.getItem('project_id');

    // Si es BOB ASSISTANT, mostrar el dashboard especializado
    if (isBobAssistant) {
        return (
            <div className={`${styles.variable_box_container} mt-3`}>
                <BobAssistantDashboard deviceId={device.id} />
            </div>
        );
    }

    const hasGPS = device?.type?.has_gps === true;

    // Para otros dispositivos, mostrar la vista normal de variables
    return (
        <div className={'mt-3'}>
            {hasGPS && device?.id && (
                <div style={{
                    marginBottom: '16px', backgroundColor: '#fff', borderRadius: '10px',
                    border: '1px solid #E2E8F0', padding: '12px',
                    width: '360px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}>
                    <LocationWidget deviceId={device.id} mqttData={data}/>
                </div>
            )}
            {(hasProductionCounter || hasConsumptionCounter) && (
                <div style={{marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap'}}>
                    {hasProductionCounter && <ProductionChart variables={data}/>}
                    {hasConsumptionCounter && <ConsumptionChart variables={data}/>}
                </div>
            )}
            <div className={styles.variable_box_container}>
                {Array.isArray(data) && data.map((value, index) => (
                    <VariableBox data={value} key={index} setLocked={setLocked} allVariables={data} onHide={onHide}/>
                ))}
            </div>
            {device?.id && projectId && (
                <ActionButtonsSection deviceId={device.id} projectId={parseInt(projectId)} variables={data}/>
            )}
        </div>
    );
}
